import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, ExternalLink, Clock, ThumbsUp, MessageSquare } from 'lucide-react';
import type { WidgetProps } from '../../../types/dashboard';

const newsItems = [
  {
    title: 'New Analytics Features Released',
    summary: 'Explore our latest analytics tools with enhanced visualization capabilities.',
    date: '2h ago',
    likes: 45,
    comments: 12,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  },
  {
    title: 'Platform Maintenance Update',
    summary: 'Scheduled maintenance completed with improved performance.',
    date: '1d ago',
    likes: 32,
    comments: 8,
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  },
  {
    title: 'Content Creation Tips',
    summary: 'Learn how to optimize your content for better engagement.',
    date: '2d ago',
    likes: 78,
    comments: 24,
    image: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  },
  {
    title: 'New Integration Available',
    summary: 'Connect with more platforms to expand your reach.',
    date: '3d ago',
    likes: 56,
    comments: 15,
    image: 'https://images.unsplash.com/photo-1558655146-d09347e92766?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }
];

export const NewsWidget: React.FC<WidgetProps> = () => {
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
            <MessageSquare className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">Latest Updates</div>
        </div>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-dark-700"
        >
          <RefreshCw className="h-4 w-4" />
        </motion.button>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto space-y-4 pr-2 no-scrollbar">
          {newsItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl hover:shadow-sm transition-all duration-200"
            >
              <div className="flex items-start space-x-4">
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {item.title}
                    </h4>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex-shrink-0 ml-2"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </motion.button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2">
                    {item.summary}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-400 dark:text-gray-500">
                      <Clock className="h-3 w-3 mr-1" />
                      {item.date}
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center text-xs text-gray-400 dark:text-gray-500">
                        <ThumbsUp className="h-3 w-3 mr-1" />
                        {item.likes}
                      </div>
                      <div className="flex items-center text-xs text-gray-400 dark:text-gray-500">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        {item.comments}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};